import React, { useState, useCallback, useRef, useEffect } from "react";
import { FaFilePdf, FaSearch, FaTimes } from "react-icons/fa";
import { useReports } from "@/api/report";
import { useResourceGallery } from "@/hooks/use-resource-gallery";
import { reportGalleryConfig } from "@/config/resource-gallery-configs";
import { useMultiSelect } from "@/hooks/use-multi-select";
import ReportItem from "./report-item";

interface ReportsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * ReportsSection component - displays PDF reports in the sidebar.
 * Shows all reports with search functionality and status indicators.
 * Uses the unified useResourceGallery hook for consistent behavior.
 */
const ReportsSection: React.FC<ReportsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const { data: reports = [] } = useReports();

  // Multi-select hook
  const { isSelected } = useMultiSelect();

  // Use the shared resource gallery hook for search, filtering, and grouping
  const {
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    filteredItems: filteredReports,
    orderedIds: orderedReportIds,
  } = useResourceGallery(reports, reportGalleryConfig);

  // Search state
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, [setSearchQuery]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && reports.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      if (e.key === "Escape" && isSearchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, reports.length, setSearchQuery]);

  if (reports.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[var(--arc-sidebar-text-secondary)]">
        <span className="text-xs">Aucun rapport</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaFilePdf size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Rapports
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        <span className="text-[10px] text-[var(--arc-sidebar-text-secondary)]">
          {filteredReports.length}
        </span>
      </div>

      {/* Search Bar */}
      {reports.length > 0 && (
        <div className="px-1 mb-3">
          <div
            className={`relative flex items-center rounded-md transition-all duration-200 ${
              isSearchFocused
                ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
                : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
            }`}
          >
            <FaSearch
              className={`absolute left-2.5 transition-colors duration-200 ${
                isSearchFocused ? "arc-sidebar-icon-active" : ""
              }`}
              size={11}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Rechercher..."
              className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
              >
                <FaTimes size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reports List */}
      {filteredReports.length > 0 && (
        <div className="space-y-0.5">
          {filteredReports.map((report) => (
            <ReportItem
              key={report.id}
              report={report}
              isSelected={isSelected(report.id)}
              orderedIds={orderedReportIds}
              onMultiSelectContextMenu={onMultiSelectContextMenu}
            />
          ))}
        </div>
      )}

      {/* No results */}
      {filteredReports.length === 0 && searchQuery && (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">Aucun résultat</span>
        </div>
      )}
    </div>
  );
};

ReportsSection.displayName = "ReportsSection";

export default ReportsSection;
